/**
 * One Bulk actions menu, for every list — list engine v3 §3.
 *
 * The thing being replaced is a bar that slid in above the table when
 * something was ticked, carrying whatever buttons that page had hand-built.
 * Three problems, and the third is the one that matters. It **moved the
 * table**, so the row you were about to tick was no longer under your
 * pointer. Each list drew its own, so Jobs and Invoices offered the same idea
 * under different words in different orders. And it could only ever act on
 * what was on the screen, which on a fifty-row page means the honest ceiling
 * of every bulk action in the product was fifty.
 *
 * So: lists **declare** what they can do, as data, and the engine draws the
 * button, the menu, the second-step pickers, the confirm, the toast and the
 * phone sheet. A new action is an entry in a catalogue, not a new control.
 *
 * The groups are fixed — **Change · Bill · Send · Take out**, then a rule and
 * the red ones — because the order is itself the information: the safe,
 * frequent things first and the irreversible one last, in the same place on
 * every list, so muscle memory is about the row rather than about which menu
 * this is.
 *
 * **Permissions are the catalogue's, not the menu's.** A page builds its
 * actions with `me` in hand and leaves out what this person may not do, so
 * there is no `permission` field here to go stale beside the real check. The
 * server re-checks every call regardless (§3.6); the menu's job is only to
 * avoid offering something that would come back 403.
 */
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { Popover } from './overlays.js'
import { Button } from './primitives.js'
import { PanelSheet, SheetButton } from './panel-sheet.js'
import { MenuSelect } from './menu-select.js'
import { IconCheck, IconChevronDown, IconChevronRight } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { usePhone } from '../lib/use-narrow.js'
import type { Selection } from '../lib/selection.js'
import type { ListArea, ListRow, UseListResult } from '../lib/use-list.js'
import type { ListView } from '@rowted/shared'

export type BulkGroup = 'change' | 'bill' | 'send' | 'takeout'

/** Fixed, and in this order. An empty group is not drawn. */
const GROUPS: { key: BulkGroup; label: string }[] = [
  { key: 'change', label: 'Change' },
  { key: 'bill', label: 'Bill' },
  { key: 'send', label: 'Send' },
  { key: 'takeout', label: 'Take out' },
]

export interface BulkContext {
  area: ListArea
  selection: Selection
  /** `selectedCount` — in `matching` mode this is bigger than `rows`. */
  count: number
  /**
   * The selected rows that happen to be **loaded**.
   *
   * Fewer than `count` whenever the selection reaches past the page, which is
   * the normal case for `matching`. An action's `note` and `appliesTo` judge
   * on these and nothing else, and say so: "2 of 3 haven't been invited" is a
   * statement about what is on screen, and the server decides the rest.
   */
  rows: ListRow[]
  view: ListView
  search: string
}

export interface BulkAction {
  /** Also the server handler key — `/bulk-actions/<key>`. */
  key: string
  group: BulkGroup
  /** Takes the count, so a label can say "Export 3 as CSV". */
  label: (n: number) => string
  icon: (props: { size?: number }) => ReactElement
  /** Red, last, and always confirms. */
  danger?: boolean
  /** Rows this cannot apply to, judged on the loaded rows only. */
  appliesTo?: (row: ListRow) => boolean
  /** The line under the label — "2 of 3 haven't been invited". */
  note?: (ctx: BulkContext) => string | undefined
  /** A second step, beside the menu on a desktop and level two of the sheet on a phone. */
  picker?: (ctx: BulkContext, done: (input: unknown) => void) => ReactNode
  confirm?: (ctx: BulkContext, input?: unknown) =>
    { title: string; body?: ReactNode; confirmLabel: string } | undefined
  /** Export and Copy emails never reach the server. */
  local?: (ctx: BulkContext) => void | Promise<void>
}

export interface BulkMenuProps {
  list: UseListResult
  actions: BulkAction[]
  ctx: BulkContext
  /** Chosen, with whatever the picker produced. The screen runs it. */
  onChoose: (action: BulkAction, input?: unknown) => void
  /** Narrow: the button keeps its glyph, pill and caret and loses the word. */
  wordless?: boolean
  /**
   * Open now, with no button of its own.
   *
   * The phone's selection bar has the button — it is the primary at the
   * bottom of the screen — so the sheet is raised from there and this draws
   * only the sheet. Same component, so the phone's menu cannot drift from
   * the desktop's.
   */
  openNow?: boolean
  onClose?: () => void
}

/**
 * The button, in three states, and the menu behind it.
 *
 * **Greyed with nothing ticked, and still openable.** That is deliberate: a
 * control that is not there until you have already done the thing it is for
 * is a control nobody discovers. Opening it with nothing ticked explains what
 * to do and offers the two ways to do it.
 */
export function BulkMenu({
  list, actions, ctx, onChoose, wordless, openNow, onClose,
}: BulkMenuProps) {
  const phone = usePhone()
  const [own, setOwn] = useState(false)
  const [picking, setPicking] = useState<BulkAction | null>(null)
  const anchor = useRef<HTMLDivElement>(null)

  const open = openNow ?? own
  const setOpen = (next: boolean) => {
    if (openNow === undefined) setOwn(next)
    else if (!next) onClose?.()
  }

  const count = ctx.count
  const on = count > 0

  const close = () => { setOpen(false); setPicking(null) }

  const choose = (action: BulkAction) => {
    if (action.picker) { setPicking(action); return }
    close()
    onChoose(action)
  }

  const body = on
    ? (
      <BulkBody
        actions={actions}
        ctx={ctx}
        onChoose={choose}
        picking={picking}
        onPicked={(action, input) => { close(); onChoose(action, input) }}
        onBackFromPicker={() => setPicking(null)}
        phone={phone}
      />
    )
    : <BulkEmpty list={list} onDone={close} />

  return (
    <div ref={anchor} className="relative">
      {/*
        * With `openNow` the phone's selection bar owns the button — it is
        * the primary at the bottom of the screen — so this draws only the
        * sheet, and the two surfaces still share one menu.
        */}
      {openNow === undefined && (
        <Button
          size="toolbar"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={wordless ? 'Bulk actions' : undefined}
          title="Bulk actions"
          className={cn('gap-1.5',
            on
              ? 'border-accent-line bg-accent-soft text-accent-dark ring-2 ring-accent-ring'
              : 'border-line bg-surface text-text-faint')}
        >
          <IconCheck size={14} />
          {!wordless && 'Bulk actions'}
          {on && (
            <span className={cn('tnum inline-flex h-[18px] min-w-[18px] items-center justify-center',
              'rounded-chip bg-accent px-1 text-[11px] font-bold text-white')}
            >
              {formatNumber(count)}
            </span>
          )}
          <IconChevronDown size={12} />
        </Button>
      )}

      {phone
        ? (
          <PanelSheet
            open={open}
            onClose={close}
            title={on ? `Act on ${formatNumber(count)} ${nounFor(ctx)}` : 'Bulk actions'}
            sub={on ? scopeLine(ctx) : undefined}
            {...(picking ? { onBack: () => setPicking(null), backLabel: 'Bulk actions' } : {})}
            footer={<SheetButton variant="default" onClick={close}>Close</SheetButton>}
          >
            {on && !picking && <BulkHead list={list} ctx={ctx} />}
            {body}
          </PanelSheet>
        )
        : (
          <Popover
            open={open}
            onClose={close}
            label="Bulk actions"
            align="right"
            width={300}
            anchor={anchor.current}
            className="rounded-menu"
          >
            {on && (
              <div className="flex h-11 items-center gap-2 border-b border-line px-3">
                <span className="shrink-0 text-accent"><IconCheck size={14} /></span>
                <span className="flex min-w-0 flex-col">
                  <b className="truncate text-card">
                    Act on {formatNumber(count)} {nounFor(ctx)}
                  </b>
                  <span className="truncate text-sub text-text-muted">{scopeLine(ctx)}</span>
                </span>
              </div>
            )}
            {body}
          </Popover>
        )}
    </div>
  )
}

/**
 * What the menu says when nothing is ticked.
 *
 * One line and two links, rather than a disabled button. "Tick rows to act on
 * them" is the sentence somebody needs, and the two links are the answer, so
 * nobody has to discover that the header checkbox has a menu in it.
 */
function BulkEmpty({ list, onDone }: { list: UseListResult; onDone: () => void }) {
  return (
    <div className="flex flex-col gap-2 p-3">
      <span className="text-cell text-text-muted">Tick rows to act on them.</span>
      <span className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => { list.selectPage(); onDone() }}
          className="text-cell font-semibold text-accent hover:text-accent-dark"
        >
          Select this page
        </button>
        <button
          type="button"
          onClick={() => { list.selectAllMatching(); onDone() }}
          className="text-cell font-semibold text-accent hover:text-accent-dark"
        >
          Select all {formatNumber(list.visibleCount)}
        </button>
      </span>
    </div>
  )
}

function BulkBody({
  actions, ctx, onChoose, picking, onPicked, onBackFromPicker, phone,
}: {
  actions: BulkAction[]
  ctx: BulkContext
  onChoose: (action: BulkAction) => void
  picking: BulkAction | null
  onPicked: (action: BulkAction, input: unknown) => void
  onBackFromPicker: () => void
  phone: boolean
}) {
  /*
   * A picker **replaces** the body rather than opening beside it on a phone,
   * and sits over it on a desktop. Either way it is the same node the action
   * supplied, so a list that wants a zone picker reaches for the control it
   * already has rather than writing a dropdown of its own.
   */
  if (picking?.picker) {
    return (
      <div className="flex flex-col">
        {!phone && (
          <button
            type="button"
            onClick={onBackFromPicker}
            className="flex h-9 shrink-0 items-center gap-1 border-b border-line px-2 text-cell font-semibold text-accent"
          >
            <IconChevronRight size={13} className="rotate-180" />
            {picking.label(ctx.count)}
          </button>
        )}
        <div className="p-2">
          {picking.picker(ctx, (input) => onPicked(picking, input))}
        </div>
      </div>
    )
  }

  const safe = actions.filter((action) => !action.danger)
  const danger = actions.filter((action) => action.danger)

  return (
    <div className={cn('flex flex-col', !phone && 'max-h-menu overflow-y-auto')}>
      {GROUPS.map((group) => {
        const mine = safe.filter((action) => action.group === group.key)
        if (mine.length === 0) return null
        return (
          <section key={group.key} className="border-b border-line-soft last:border-b-0">
            <h4 className="px-3 pb-1 pt-2 text-label text-text-faint">{group.label}</h4>
            {mine.map((action) => (
              <BulkRow key={action.key} action={action} ctx={ctx} onChoose={onChoose} phone={phone} />
            ))}
          </section>
        )
      })}
      {danger.length > 0 && (
        <section className="border-t border-line">
          {danger.map((action) => (
            <BulkRow key={action.key} action={action} ctx={ctx} onChoose={onChoose} phone={phone} />
          ))}
        </section>
      )}
    </div>
  )
}

function BulkRow({
  action, ctx, onChoose, phone,
}: {
  action: BulkAction
  ctx: BulkContext
  onChoose: (action: BulkAction) => void
  phone: boolean
}) {
  const Icon = action.icon
  const note = action.note?.(ctx)

  /*
   * Dead only when it applies to **none** of the loaded rows. Applying to
   * some is a live row with a note saying how many — because the server is
   * what decides, and a client that greys out an action over fifty rows it
   * can see would be greying it out over four hundred it cannot.
   */
  const dead = action.appliesTo !== undefined
    && ctx.rows.length > 0
    && !ctx.rows.some((row) => action.appliesTo!(row))

  return (
    <button
      type="button"
      disabled={dead}
      onClick={() => onChoose(action)}
      className={cn('flex w-full items-center gap-2.5 px-3 text-left',
        phone ? 'min-h-[52px] py-2' : note ? 'min-h-[46px] py-1.5' : 'h-[34px]',
        dead ? 'cursor-not-allowed opacity-55' : 'hover:bg-surface-alt',
        action.danger ? 'text-danger' : 'text-text')}
    >
      <span aria-hidden className={cn('shrink-0', action.danger ? 'text-danger' : 'text-text-muted')}>
        <Icon size={15} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn('truncate', phone ? 'text-[15px] font-medium' : 'text-cell')}>
          {action.label(ctx.count)}
        </span>
        {note && (
          <span className={cn('truncate text-sub',
            dead ? 'text-text-faint' : 'text-warn')}
          >
            {note}
          </span>
        )}
      </span>
      {action.picker && (
        <span aria-hidden className="shrink-0 text-text-faint"><IconChevronRight size={14} /></span>
      )}
    </button>
  )
}

/** "On this page" / "All 128 that match · 2 left out" — what is about to happen. */
export function scopeLine(ctx: BulkContext): string {
  if (ctx.selection.mode === 'ids') {
    return ctx.count > ctx.rows.length ? 'Ticked across pages' : 'On this page'
  }
  const left = ctx.selection.excluded.size
  const all = `All ${formatNumber(ctx.count)} that match`
  return left > 0 ? `${all} · ${formatNumber(left)} left out` : all
}

/**
 * What the sheet is acting on, at the top of it (§7c).
 *
 * The desktop menu does not need this: the toolbar it hangs off already says
 * "3 selected · Select all 422" two inches above, in the slot the count was
 * in. A sheet covers that — it comes up over the whole list — so without this
 * the only number on screen is the one in the header, and the one thing
 * somebody wants at that moment, *am I about to act on three or on four
 * hundred*, is behind a dismissal.
 *
 * `Select all N` appears only while there is more to select. Offering it on a
 * selection that already is everything is a control that does nothing, and in
 * `matching` mode the line says so instead.
 */
function BulkHead({ list, ctx }: { list: UseListResult; ctx: BulkContext }) {
  const more = ctx.selection.mode === 'ids' && ctx.count < list.visibleCount

  return (
    <div className="flex items-center gap-2.5 border-b border-line-faint bg-surface-alt px-4 py-3">
      <span className={cn('flex h-[22px] w-[22px] shrink-0 items-center justify-center',
        'rounded-field bg-accent text-white')}
      >
        <IconCheck size={14} />
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <b className="text-[15px]">
          {ctx.selection.mode === 'matching'
            ? `All ${formatNumber(ctx.count)} selected`
            : `${formatNumber(ctx.count)} selected`}
        </b>
        <span className="text-sub text-text-muted">
          of {formatNumber(list.visibleCount)} {nounFor({ ...ctx, count: list.visibleCount })}
        </span>
      </span>
      {more && (
        <button
          type="button"
          onClick={list.selectAllMatching}
          className="ml-auto shrink-0 text-cell font-semibold text-accent"
        >
          Select all {formatNumber(list.visibleCount)}
        </button>
      )}
    </div>
  )
}

/** The area's own word, so a menu says "3 contacts" rather than "3 records". */
export function nounFor(ctx: BulkContext): string {
  const one = AREA_NOUNS[ctx.area] ?? 'record'
  return ctx.count === 1 ? one : `${one}s`
}

const AREA_NOUNS: Partial<Record<ListArea, string>> = {
  contacts: 'contact',
  properties: 'property',
  jobs: 'job',
  invoices: 'invoice',
  visits: 'visit',
  equipment: 'machine',
}

/**
 * Picking one record for a bulk action — "Set company…", "Set zone…".
 *
 * It asks the **list** rather than a lookup endpoint of its own
 * (`list.lookup(field, query)`), which is the same search the quick filter's
 * relation control uses and therefore the same set of records, narrowed by
 * the same scope. A second search over the same thing is a second answer
 * waiting to differ.
 *
 * `MenuSelect` rather than a new dropdown, per §3.2: pickers are the controls
 * that already exist.
 */
export function BulkRecordPicker({
  list, field, label, clearLabel, onPick,
}: {
  list: UseListResult
  /** The relation field on this area — `account`, `zone`. */
  field: string
  label: string
  /** The word for "none of them", where clearing is a real answer. */
  clearLabel?: string
  onPick: (id: string | null) => void
}) {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([])

  useEffect(() => {
    let cancelled = false
    void list.lookup(field, '')
      .then((rows) => {
        if (!cancelled) setOptions(rows.map((row) => ({ value: row.id, label: row.label })))
      })
      .catch(() => { if (!cancelled) setOptions([]) })
    return () => { cancelled = true }
  }, [list, field])

  return (
    <MenuSelect
      label={label}
      value={null}
      onChange={(value) => onPick(value)}
      options={options}
      placeholder={label}
      minWidth={240}
      className="w-full"
      {...(clearLabel ? { clearable: true, clearLabel } : {})}
    />
  )
}
